import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import SessionTable from './components/SessionTable';
import SessionDetailsModal from './components/SessionDetailsModal';
import SessionFilters from './components/SessionFilters';
import SessionMap from './components/SessionMap';
import BulkActionsToolbar from './components/BulkActionsToolbar';
import * as sessionsApi from 'shared/api/auth/sessionsApi';
import { useAuth } from 'shared/context/AuthContext';
import { notify } from 'shared/utils/notify';

const SessionManagement = () => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isMapViewOpen, setIsMapViewOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    deviceType: 'all',
    location: 'all',
    status: 'all',
    timeRange: '24h'
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'lastActivity',
    direction: 'desc'
  });

  // Fetch sessions from API
  const fetchSessions = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await sessionsApi.getSessions();
      // Transform API response to component format
      const transformedSessions = data.map((session, index) => ({
        id: session.sessionId,
        userId: session.userId,
        userName: session.userName || 'Пользователь',
        userEmail: session.userEmail || '',
        deviceType: detectDeviceType(session.userAgent),
        deviceName: session.deviceInfo || parseUserAgent(session.userAgent),
        ipAddress: session.ipAddress || 'Unknown',
        location: session.location || 'Неизвестно',
        coordinates: session.coordinates || null,
        loginTime: new Date(session.createdAt),
        lastActivity: new Date(session.lastActivityAt || session.createdAt),
        status: session.isRevoked ? 'expired' : (session.isActive ? 'active' : 'idle'),
        riskLevel: session.riskLevel || 'low',
        sessionDuration: formatDuration(new Date(session.createdAt)),
        actions: [],
        userAgent: session.userAgent || '',
        isCurrent: session.isCurrent || false
      }));
      setSessions(transformedSessions);
      setFilteredSessions(transformedSessions);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
      setError('Не удалось загрузить сессии');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Helper functions for session data transformation
  const detectDeviceType = (userAgent) => {
    if (!userAgent) return 'Desktop';
    if (/mobile/i.test(userAgent)) return 'Mobile';
    if (/tablet|ipad/i.test(userAgent)) return 'Tablet';
    return 'Desktop';
  };

  const parseUserAgent = (userAgent) => {
    if (!userAgent) return 'Unknown Device';
    // Simple UA parsing
    if (/windows/i.test(userAgent)) return 'Windows - ' + (userAgent.match(/Chrome|Firefox|Safari|Edge/)?.[0] || 'Browser');
    if (/macintosh|mac os/i.test(userAgent)) return 'macOS - ' + (userAgent.match(/Chrome|Firefox|Safari/)?.[0] || 'Browser');
    if (/iphone/i.test(userAgent)) return 'iPhone - Safari';
    if (/android/i.test(userAgent)) return 'Android - Chrome';
    if (/linux/i.test(userAgent)) return 'Linux - ' + (userAgent.match(/Chrome|Firefox/)?.[0] || 'Browser');
    return 'Unknown Device';
  };

  const formatDuration = (startTime) => {
    const diff = Date.now() - startTime.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  useEffect(() => {
    let filtered = [...sessions];

    // Apply search filter
    if (filters.search) {
      filtered = filtered.filter(session =>
        session.userName.toLowerCase().includes(filters.search.toLowerCase()) ||
        session.userEmail.toLowerCase().includes(filters.search.toLowerCase()) ||
        session.ipAddress.includes(filters.search) ||
        session.location.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    // Apply device type filter
    if (filters.deviceType !== 'all') {
      filtered = filtered.filter(session =>
        session.deviceType.toLowerCase() === filters.deviceType.toLowerCase()
      );
    }

    // Apply status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(session => session.status === filters.status);
    }

    // Apply time range filter
    const now = new Date();
    if (filters.timeRange !== 'all') {
      const timeRanges = {
        '1h': 60 * 60 * 1000,
        '24h': 24 * 60 * 60 * 1000,
        '7d': 7 * 24 * 60 * 60 * 1000,
        '30d': 30 * 24 * 60 * 60 * 1000
      };
      const timeLimit = timeRanges[filters.timeRange];
      if (timeLimit) {
        filtered = filtered.filter(session =>
          now - session.loginTime <= timeLimit
        );
      }
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (aValue instanceof Date) {
        aValue = aValue.getTime();
        bValue = bValue.getTime();
      }

      if (sortConfig.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredSessions(filtered);
  }, [sessions, filters, sortConfig]);

  const handleSessionSelect = (sessionId) => {
    setSelectedSessions(prev => {
      if (prev.includes(sessionId)) {
        return prev.filter(id => id !== sessionId);
      } else {
        return [...prev, sessionId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedSessions.length === filteredSessions.length) {
      setSelectedSessions([]);
    } else {
      setSelectedSessions(filteredSessions.map(session => session.id));
    }
  };

  const handleSessionDetails = (session) => {
    setSelectedSession(session);
    setIsDetailsModalOpen(true);
  };

  const handleTerminateSession = async (sessionId) => {
    try {
      await sessionsApi.terminateSession(sessionId);
      setSessions(prev => prev.filter(session => session.id !== sessionId));
      setSelectedSessions(prev => prev.filter(id => id !== sessionId));
      notify.success('Сессия завершена');
    } catch (err) {
      console.error('Failed to terminate session:', err);
      setError('Не удалось завершить сессию');
    }
  };

  const handleBulkTerminate = async () => {
    try {
      const count = selectedSessions.length;
      // Terminate sessions one by one (API may not support bulk)
      await Promise.all(selectedSessions.map(id => sessionsApi.terminateSession(id)));
      setSessions(prev => prev.filter(session => !selectedSessions.includes(session.id)));
      setSelectedSessions([]);
      notify.success(`Завершено сессий: ${count}`);
    } catch (err) {
      console.error('Failed to terminate sessions:', err);
      setError('Не удалось завершить выбранные сессии');
    }
  };

  const handleTerminateAllSessions = async () => {
    try {
      await sessionsApi.terminateAllSessions();
      // Keep only current session
      setSessions(prev => prev.filter(session => session.isCurrent));
      setSelectedSessions([]);
      notify.success('Все сессии завершены');
    } catch (err) {
      console.error('Failed to terminate all sessions:', err);
      setError('Не удалось завершить все сессии');
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const getStatusStats = () => {
    const stats = {
      total: sessions.length,
      active: sessions.filter(s => s.status === 'active').length,
      idle: sessions.filter(s => s.status === 'idle').length,
      expired: sessions.filter(s => s.status === 'expired').length,
      suspicious: sessions.filter(s => s.status === 'suspicious').length
    };
    return stats;
  };

  const stats = getStatusStats();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">Загрузка сессий...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="p-6">
        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-md flex items-center justify-between">
            <p className="text-sm text-error">{error}</p>
            <button onClick={() => setError('')} className="text-error hover:text-error-700">
              <Icon name="X" size={16} />
            </button>
          </div>
        )}

        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-text-secondary mb-6">
          <span>Dashboard</span>
          <Icon name="ChevronRight" size={16} />
          <span>Security</span>
          <Icon name="ChevronRight" size={16} />
          <span className="text-text-primary font-medium">Session Management</span>
        </nav>

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-semibold text-text-primary mb-2">
              Управление сессиями
            </h1>
            <p className="text-text-secondary">
              Мониторинг и контроль активных пользовательских сессий в реальном времени
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsMapViewOpen(!isMapViewOpen)}
              className={`
                px-4 py-2 rounded-md border transition-all duration-150 hover-lift
                ${isMapViewOpen
                  ? 'bg-primary text-white border-primary' :'bg-surface text-text-primary border-secondary-300 hover:bg-secondary-50'
                }
              `}
            >
              <Icon name="Map" size={16} className="mr-2" />
              Карта сессий
            </button>
            <button className="px-4 py-2 bg-accent text-white rounded-md hover:bg-accent-700 transition-colors duration-150 hover-lift">
              <Icon name="Download" size={16} className="mr-2" />
              Экспорт логов
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className="bg-surface rounded-lg border border-secondary-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary">Всего сессий</p>
                <p className="text-2xl font-semibold text-text-primary">{stats.total}</p>
              </div>
              <div className="w-10 h-10 bg-secondary-100 rounded-lg flex items-center justify-center">
                <Icon name="Users" size={20} className="text-secondary-600" />
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-lg border border-secondary-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary">Активные</p>
                <p className="text-2xl font-semibold text-success">{stats.active}</p>
              </div>
              <div className="w-10 h-10 bg-success-100 rounded-lg flex items-center justify-center">
                <Icon name="Activity" size={20} className="text-success-600" />
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-lg border border-secondary-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary">Неактивные</p>
                <p className="text-2xl font-semibold text-warning">{stats.idle}</p>
              </div>
              <div className="w-10 h-10 bg-warning-100 rounded-lg flex items-center justify-center">
                <Icon name="Clock" size={20} className="text-warning-600" />
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-lg border border-secondary-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary">Истекшие</p>
                <p className="text-2xl font-semibold text-secondary-500">{stats.expired}</p>
              </div>
              <div className="w-10 h-10 bg-secondary-100 rounded-lg flex items-center justify-center">
                <Icon name="XCircle" size={20} className="text-secondary-600" />
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-lg border border-secondary-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary">Подозрительные</p>
                <p className="text-2xl font-semibold text-error">{stats.suspicious}</p>
              </div>
              <div className="w-10 h-10 bg-error-100 rounded-lg flex items-center justify-center">
                <Icon name="AlertTriangle" size={20} className="text-error-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Map View */}
        {isMapViewOpen && (
          <div className="bg-surface rounded-lg border border-secondary-200 p-6 mb-6">
            <SessionMap sessions={filteredSessions} />
          </div>
        )}

        {/* Filters */}
        <div className="bg-surface rounded-lg border border-secondary-200 p-6 mb-6">
          <SessionFilters
            filters={filters}
            onFiltersChange={setFilters}
            sessionCount={filteredSessions.length}
          />
        </div>

        {/* Bulk Actions Toolbar */}
        {selectedSessions.length > 0 && (
          <BulkActionsToolbar
            selectedCount={selectedSessions.length}
            onBulkTerminate={handleBulkTerminate}
            onClearSelection={() => setSelectedSessions([])}
          />
        )}

        {/* Session Table */}
        <div className="bg-surface rounded-lg border border-secondary-200">
          <SessionTable
            sessions={filteredSessions}
            selectedSessions={selectedSessions}
            sortConfig={sortConfig}
            onSessionSelect={handleSessionSelect}
            onSelectAll={handleSelectAll}
            onSort={handleSort}
            onSessionDetails={handleSessionDetails}
            onTerminateSession={handleTerminateSession}
          />
        </div>

        {/* Session Details Modal */}
        {isDetailsModalOpen && selectedSession && (
          <SessionDetailsModal
            session={selectedSession}
            isOpen={isDetailsModalOpen}
            onClose={() => {
              setIsDetailsModalOpen(false);
              setSelectedSession(null);
            }}
            onTerminate={() => {
              handleTerminateSession(selectedSession.id);
              setIsDetailsModalOpen(false);
              setSelectedSession(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default SessionManagement;